import { CssBaseline, createTheme, ThemeProvider } from "@mui/material";
import "@fontsource/roboto/300.css";
import "@fontsource/roboto/400.css";
import "@fontsource/roboto/500.css";
import "@fontsource/roboto/700.css";
import { StrictMode, Suspense } from "react";
import { createRoot } from "react-dom/client";
import { MemoryRouter, useRoutes } from "react-router";
import { configureApiClient } from "./api/client-config.ts";
import { Loader } from "./components/loader/index.tsx";
import { GlobalErrorBoundary } from "./global-error-boundary.tsx";
import { ConfigProvider } from "./lib/config/hooks.tsx";
import { getConfig } from "./lib/config/index.ts";
import { ObservabilityProvider } from "./lib/observability/hooks.tsx";
import { observability } from "./lib/observability/index.ts";
import { handleDialogCallback, initOfficeEnvironment, isOfficeDialog } from "./lib/office/index.ts";
import { QueryClientProvider, queryClient, ReactQueryDevtools } from "./lib/react-query/index.ts";
import { routes } from "./routes.tsx";

const theme = createTheme({
  palette: {
    mode: "light",
    primary: {
      main: "#A31C54",
      light: "#FAEAEA",
    },
    warning: {
      main: "#D67058",
    },
  },
});

function AppRoutes() {
  return <Suspense fallback={<Loader />}>{useRoutes(routes)}</Suspense>;
}

async function bootstrap() {
  if (typeof Office !== "undefined") {
    const info = await Office.onReady();
    initOfficeEnvironment(info.host);

    // If we're inside the Office dialog after auth completed, extract the
    // token and send it back to the parent taskpane — don't render the app.
    if (isOfficeDialog()) {
      await handleDialogCallback();
      return;
    }
  }

  const config = getConfig();
  configureApiClient();

  const { apm } = observability.start({
    environment: config.ilEnv,
    buildVersion: config.buildVersion,
  });

  // biome-ignore lint/style/noNonNullAssertion: we are sure that root exists
  createRoot(document.getElementById("root")!).render(
    <StrictMode>
      <ThemeProvider theme={theme}>
        <CssBaseline />
        <ObservabilityProvider apm={apm}>
          <ConfigProvider config={config}>
            <GlobalErrorBoundary apm={apm}>
              <QueryClientProvider client={queryClient}>
                <ReactQueryDevtools initialIsOpen={false} />
                <MemoryRouter>
                  <AppRoutes />
                </MemoryRouter>
              </QueryClientProvider>
            </GlobalErrorBoundary>
          </ConfigProvider>
        </ObservabilityProvider>
      </ThemeProvider>
    </StrictMode>,
  );
}

bootstrap();
